import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using scopes for elegant JAMStack permissions",
  "description": "",
  "published": "2020-06-15T00:00:00.000Z",
  "redirect_from": ["/blog/using-scopes-for-elegant-jamstack-permissions/swizec/9431"],
  "categories": "Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1592236049540.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`About a month ago I wrote about `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/add-granular-role-based-access-to-your-jamstack-app/swizec/9418"
      }}>{`Adding granular role-based access to your JAMStack app`}</a>{`. That worked okay.`}</p>
    <p>{`Use something like `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` to authenticate users, add some roles, then check those roles in your app. Often at the main `}<inlineCode parentName="p">{`<Layout>`}</inlineCode>{` level.`}</p>
    <p>{`You get an app that sometimes asks for additional permissions.`}</p>
    <p><img parentName="p" {...{
        "src": "/f7da90ff549b239f93f00f81ea19e851/5eSMcCw.gif",
        "alt": null
      }}></img></p>
    <p>{`But this approach had 2 warts:`}</p>
    <ol>
      <li parentName="ol">{`Lots of people mentioned that `}<em parentName="li">{`scopes`}</em>{` in practice scale better than roles`}</li>
      <li parentName="ol">{`You needed this gnarly code somewhere in your app`}</li>
    </ol>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20LockedContent%20%3D%20(props)%20%3D%3E%20%7B%0A%20%20const%20allowUnauth%20%3D%20UNAUTH_PAGES.includes(currentLocation(props))%0A%20%20const%20%7B%20isAuthorized%2C%20isAuthenticated%20%7D%20%3D%20useAuth()%0A%0A%20%20if%20(!allowUnauth%20%26%26%20!isAuthenticated())%20%7B%0A%20%20%20%20%2F%2F%20not%20even%20logged%20in%2C%20ask%20for%20login%0A%20%20%20%20return%20%3CPleaseLogin%20%2F%3E%0A%20%20%7D%20else%20%7B%0A%20%20%20%20if%20(isFindYourIdeaPage(props))%20%7B%0A%20%20%20%20%20%20if%20(isAuthorized(%22FindYourIdea%22))%20%7B%0A%20%20%20%20%20%20%20%20%2F%2F%20FindYourIdea%20page%2C%20access%20-%3E%20show%0A%20%20%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%20%20%7D%20else%20%7B%0A%20%20%20%20%20%20%20%20%2F%2F%20FindYourIdea%20page%2C%20no%20access%20-%3E%20buy%0A%20%20%20%20%20%20%20%20return%20%3CPleasePurchase%20findYourIdea%20%2F%3E%0A%20%20%20%20%20%20%7D%0A%20%20%20%20%7D%20else%20if%20(isAuthorized(%22Student%22))%20%7B%0A%20%20%20%20%20%20%2F%2F%20ServerlessReactDev%20page%2C%20acces%20-%3E%20show%0A%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%7D%20else%20if%20(allowUnauth)%20%7B%0A%20%20%20%20%20%20%2F%2F%20no%20auth%20required%20-%3E%20show%0A%20%20%20%20%20%20return%20%3CContent%20%7B...props%7D%20%2F%3E%0A%20%20%20%20%7D%20else%20%7B%0A%20%20%20%20%20%20%2F%2F%20ServerlessReactDev%20page%2C%20no%20access%20-%3E%20buy%0A%20%20%20%20%20%20return%20%3CPleasePurchase%20%2F%3E%0A%20%20%20%20%7D%0A%20%20%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "638px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "105.38116591928251%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'421\\'%20viewBox=\\'0%200%20400%20421\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2031l-1%20181c0%20170%200%20179%202%20180h356c2-1%202-10%202-180l-1-181c-2-2-356-2-358%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/462fc1e72e9198d40a5bb9e6c1749720/ca0a1/techletter.app-screenshot-1592236049540.webp 223w", "/static/462fc1e72e9198d40a5bb9e6c1749720/75680/techletter.app-screenshot-1592236049540.webp 445w", "/static/462fc1e72e9198d40a5bb9e6c1749720/a2d8a/techletter.app-screenshot-1592236049540.webp 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/462fc1e72e9198d40a5bb9e6c1749720/e92b6/techletter.app-screenshot-1592236049540.png 223w", "/static/462fc1e72e9198d40a5bb9e6c1749720/e66bf/techletter.app-screenshot-1592236049540.png 445w", "/static/462fc1e72e9198d40a5bb9e6c1749720/41be6/techletter.app-screenshot-1592236049540.png 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/462fc1e72e9198d40a5bb9e6c1749720/41be6/techletter.app-screenshot-1592236049540.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p><img parentName="p" {...{
        "src": "/dc7e4436969251f5ffbc64b393aac8d9/media-y7kvOYLzas6Ag-giphy-downsized-medium.gif",
        "alt": "fillion giphy"
      }}></img></p>
    <h2 {...{
      "id": "scopes-bring-more-elegance"
    }}>{`Scopes bring more elegance`}</h2>
    <p>{`Wanted to use this approach for my `}<a parentName="p" {...{
        "href": "https://reactfordataviz.com"
      }}>{`React for Dataviz`}</a>{` course, which has 3 tiers on top of free stuff, and my head began to spin. No way this was gonna work. 😅`}</p>
    <p>{`I set out to find a better way:`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=fPqJBs6O3_U"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "56.05381165919282%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'225\\'%20viewBox=\\'0%200%20400%20225\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M196%20105c-6%200-7%201-7%207%200%207%201%207%209%208%2011%200%2012-1%2012-8v-6l-5-1h-9M5%20162H0v63h401v-32c0-31%200-31-1-5v27h-69a1481%201481%200%2000-100-1h-3v-14l-1%207c0%206-1%208-2%208s-2-4-2-24v-24l-91-1-90-1c0-4%201-4-16-4l-21%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b94032a43abed8b69b6b106bb271a8a1/ca0a1/techletter.app-screenshot-1592236049300.webp 223w", "/static/b94032a43abed8b69b6b106bb271a8a1/75680/techletter.app-screenshot-1592236049300.webp 445w", "/static/b94032a43abed8b69b6b106bb271a8a1/0ba47/techletter.app-screenshot-1592236049300.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/b94032a43abed8b69b6b106bb271a8a1/e92b6/techletter.app-screenshot-1592236049300.png 223w", "/static/b94032a43abed8b69b6b106bb271a8a1/e66bf/techletter.app-screenshot-1592236049300.png 445w", "/static/b94032a43abed8b69b6b106bb271a8a1/6af66/techletter.app-screenshot-1592236049300.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/b94032a43abed8b69b6b106bb271a8a1/6af66/techletter.app-screenshot-1592236049300.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And I found one! Scopes.`}</p>
    <p>{`A scope is no different than a role – a string attached to the user. The `}<em parentName="p">{`semantics`}</em>{` of scopes are different though.`}</p>
    <p>{`Instead of answering `}<em parentName="p">{`"Who is this user?"`}</em>{` a scope answers `}<em parentName="p">{`"What can this user do?"`}</em>{`. Small difference, big impact.`}</p>
    <p>{`And when you move permission checking to the router-level, the result is quite elegant. I think.`}</p>
    <p>{`Haven't tried it with NextJS, with Gatsby you'd do something like this 👇`}</p>
    <h3 {...{
      "id": "1-wrap-the-root-of-your-tree"
    }}>{`1. wrap the root of your tree`}</h3>
    <p>{`Permission-checking happens as high up in your component tree as possible. For Gatsby that's the `}<inlineCode parentName="p">{`wrapPageElement`}</inlineCode>{` method. I use the same method for `}<inlineCode parentName="p">{`gatsby-ssr`}</inlineCode>{` and `}<inlineCode parentName="p">{`gatsby-browser`}</inlineCode>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=export%20const%20wrapPageElement%20%3D%20(%7B%20element%2C%20...props%20%7D)%20%3D%3E%20(%0A%20%20%3CAuthProvider%0A%20%20%20%20navigate%3D%7Bnavigate%7D%0A%20%20%20%20auth0_domain%3D%22serverlessreactcourse.auth0.com%22%0A%20%20%20%20auth0_client_id%3D%22...%22%0A%20%20%20%20auth0_params%3D%7B%7B%0A%20%20%20%20%20%20scope%3A%20%22openid%20profile%20email%20user_metadata%22%2C%0A%20%20%20%20%7D%7D%0A%20%20%20%20customPropertyNamespace%3D%22https%3A%2F%2Fserverlessreact.dev%22%0A%20%20%3E%0A%20%20%20%20%3CMyRouter%20element%3D%7Belement%7D%20%7B...props%7D%20%7B...props.props%7D%20%2F%3E%0A%20%20%3C%2FAuthProvider%3E%0A)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "598px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "67.2645739910314%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'269\\'%20viewBox=\\'0%200%20400%20269\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M22%2034l-1%20102c0%2092%200%20100%202%20101%201%201%2030%202%20177%202s176-1%20177-2c2-1%202-9%202-101l-1-102c-2-3-354-3-356%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/916c4d0b1260b8216affcd8a04ed8348/ca0a1/techletter.app-screenshot-1592236047097.webp 223w", "/static/916c4d0b1260b8216affcd8a04ed8348/75680/techletter.app-screenshot-1592236047097.webp 445w", "/static/916c4d0b1260b8216affcd8a04ed8348/83245/techletter.app-screenshot-1592236047097.webp 598w"],
                "sizes": "(max-width: 598px) 100vw, 598px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/916c4d0b1260b8216affcd8a04ed8348/e92b6/techletter.app-screenshot-1592236047097.png 223w", "/static/916c4d0b1260b8216affcd8a04ed8348/e66bf/techletter.app-screenshot-1592236047097.png 445w", "/static/916c4d0b1260b8216affcd8a04ed8348/0c69d/techletter.app-screenshot-1592236047097.png 598w"],
                "sizes": "(max-width: 598px) 100vw, 598px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/916c4d0b1260b8216affcd8a04ed8348/0c69d/techletter.app-screenshot-1592236047097.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Wrap everything in useAuth's `}<inlineCode parentName="p">{`<AuthProvider>`}</inlineCode>{` then render the `}<inlineCode parentName="p">{`<MyRouter>`}</inlineCode>{` component making sure to pass the element and all props.`}</p>
    <h3 {...{
      "id": "2-a-simple-router"
    }}>{`2. a simple router`}</h3>
    <p>{`Next you need a router to map paths to components. Gatsby comes with `}<a parentName="p" {...{
        "href": "https://reach.tech/router"
      }}>{`reach/router`}</a>{` built-in so that seems like a good choice.`}</p>
    <p>{`Unfortunately I couldn't get it to work reliably. Kept matching incorrect routes. The integration isn't as tight as I hoped.`}</p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/ryanflorence/status/1271145521671884801"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "42.600896860986545%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'171\\'%20viewBox=\\'0%200%20400%20171\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M24%2017c-1%201-2%208-1%2012%201%202%201%203-2%204-4%201-4%203%200%205%205%203%2016%201%2016-3%200-1-5-3-6-2l-1-2c4-11%200-18-6-14\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/02b3122b08c27489debcd33cd4661df6/ca0a1/techletter.app-screenshot-1592236047596.webp 223w", "/static/02b3122b08c27489debcd33cd4661df6/75680/techletter.app-screenshot-1592236047596.webp 445w", "/static/02b3122b08c27489debcd33cd4661df6/0ba47/techletter.app-screenshot-1592236047596.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/02b3122b08c27489debcd33cd4661df6/e92b6/techletter.app-screenshot-1592236047596.png 223w", "/static/02b3122b08c27489debcd33cd4661df6/e66bf/techletter.app-screenshot-1592236047596.png 445w", "/static/02b3122b08c27489debcd33cd4661df6/6af66/techletter.app-screenshot-1592236047596.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/02b3122b08c27489debcd33cd4661df6/6af66/techletter.app-screenshot-1592236047596.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/ryanflorence/status/1271147083093782528"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "55.15695067264574%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'221\\'%20viewBox=\\'0%200%20400%20221\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M25%2017c-2%202-3%208-2%2012%201%203%201%203-2%204-5%201-5%202-1%205%203%203%2011%203%2015%200l2-3-3-1-3-1-1%201%201-4c2-6%201-10-1-12s-3-2-5-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2ab1ae3c31ec4a03c94914231f85dd55/ca0a1/techletter.app-screenshot-1592236047613.webp 223w", "/static/2ab1ae3c31ec4a03c94914231f85dd55/75680/techletter.app-screenshot-1592236047613.webp 445w", "/static/2ab1ae3c31ec4a03c94914231f85dd55/0ba47/techletter.app-screenshot-1592236047613.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2ab1ae3c31ec4a03c94914231f85dd55/e92b6/techletter.app-screenshot-1592236047613.png 223w", "/static/2ab1ae3c31ec4a03c94914231f85dd55/e66bf/techletter.app-screenshot-1592236047613.png 445w", "/static/2ab1ae3c31ec4a03c94914231f85dd55/6af66/techletter.app-screenshot-1592236047613.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/2ab1ae3c31ec4a03c94914231f85dd55/6af66/techletter.app-screenshot-1592236047613.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`But that's okay. Turns out building your own basic router isn't so hard.`}</p>
    <p>{`Here's mine:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20MyRouter%20%3D%20(%7B%20element%2C%20...props%20%7D)%20%3D%3E%20%7B%0A%20%20const%20scopedPages%20%3D%20Object.keys(SCOPE_PAGE_MAP)%0A%20%20const%20scopedKey%20%3D%20scopedPages.find((page)%20%3D%3E%20minimatch(props.path%2C%20page))%0A%0A%20%20if%20(scopedKey)%20%7B%0A%20%20%20%20return%20(%0A%20%20%20%20%20%20%3CScopedRoute%0A%20%20%20%20%20%20%20%20element%3D%7Belement%7D%0A%20%20%20%20%20%20%20%20scopes%3D%7BSCOPE_PAGE_MAP%5BscopedKey%5D%7D%0A%20%20%20%20%20%20%20%20%7B...props%7D%0A%20%20%20%20%20%20%2F%3E%0A%20%20%20%20)%0A%20%20%7D%20else%20%7B%0A%20%20%20%20return%20%3CDefault%20element%3D%7Belement%7D%20%7B...props%7D%20%2F%3E%0A%20%20%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "702px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "65.02242152466367%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'260\\'%20viewBox=\\'0%200%20400%20260\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2028c-2%201-2%207-2%20102l1%20103c2%202%20360%202%20362%200l1-103c0-99%200-101-2-102H20\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/99d65a9074e3bbffa6508e7c7c4cb728/ca0a1/techletter.app-screenshot-1592236046783.webp 223w", "/static/99d65a9074e3bbffa6508e7c7c4cb728/75680/techletter.app-screenshot-1592236046783.webp 445w", "/static/99d65a9074e3bbffa6508e7c7c4cb728/de2ca/techletter.app-screenshot-1592236046783.webp 702w"],
                "sizes": "(max-width: 702px) 100vw, 702px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/99d65a9074e3bbffa6508e7c7c4cb728/e92b6/techletter.app-screenshot-1592236046783.png 223w", "/static/99d65a9074e3bbffa6508e7c7c4cb728/e66bf/techletter.app-screenshot-1592236046783.png 445w", "/static/99d65a9074e3bbffa6508e7c7c4cb728/d6331/techletter.app-screenshot-1592236046783.png 702w"],
                "sizes": "(max-width: 702px) 100vw, 702px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/99d65a9074e3bbffa6508e7c7c4cb728/d6331/techletter.app-screenshot-1592236046783.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`We use a `}<inlineCode parentName="p">{`SCOPE_PAGE_MAP`}</inlineCode>{` that maps glob'd locations to their scopes. Use the `}<inlineCode parentName="p">{`minimatch`}</inlineCode>{` library to find a matching path.`}</p>
    <p>{`If path is found, render `}<inlineCode parentName="p">{`<ScopedRoute>`}</inlineCode>{`, otherwise render `}<inlineCode parentName="p">{`<Default>`}</inlineCode>{`.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`SCOPE_PAGE_MAP`}</inlineCode>{` is a long list like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20SCOPE_PAGE_MAP%20%3D%20%7B%0A%20%20%22%2Fintroduction%2F*%22%3A%20%5B%22RDV_Basic%22%2C%20%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%22%2Fbuilding-blocks%2F*%22%3A%20%5B%22RDV_Basic%22%2C%20%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%22%2Fd3-quick-intro%2F*%22%3A%20%5B%22RDV_Basic%22%2C%20%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%22%2Freact-d3%2F*%22%3A%20%5B%22RDV_Basic%22%2C%20%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%22%2Fanimation%2F*%22%3A%20%5B%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%22%2Fball-game%2F*%22%3A%20%5B%22RDV_Full%22%2C%20%22RDV_AllExtras%22%5D%2C%0A%20%20%2F%2F%20..."
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "638px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "48.87892376681614%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'196\\'%20viewBox=\\'0%200%20400%20196\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2031c-2%202-2%20133%200%20135s356%202%20358%200%202-133%200-135-356-2-358%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6ba92eda27963f6d3abf1f7dd343aba6/ca0a1/techletter.app-screenshot-1592236046982.webp 223w", "/static/6ba92eda27963f6d3abf1f7dd343aba6/75680/techletter.app-screenshot-1592236046982.webp 445w", "/static/6ba92eda27963f6d3abf1f7dd343aba6/a2d8a/techletter.app-screenshot-1592236046982.webp 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6ba92eda27963f6d3abf1f7dd343aba6/e92b6/techletter.app-screenshot-1592236046982.png 223w", "/static/6ba92eda27963f6d3abf1f7dd343aba6/e66bf/techletter.app-screenshot-1592236046982.png 445w", "/static/6ba92eda27963f6d3abf1f7dd343aba6/41be6/techletter.app-screenshot-1592236046982.png 638w"],
                "sizes": "(max-width: 638px) 100vw, 638px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6ba92eda27963f6d3abf1f7dd343aba6/41be6/techletter.app-screenshot-1592236046982.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This part is tedious. Thinking of ways to move this info into MDX frontmatter that you can query with GraphQL.`}</p>
    <p>{`Perhaps an idea for a Gatsby plugin 🤔`}</p>
    <h3 {...{
      "id": "3-default-route"
    }}>{`3. Default route`}</h3>
    <p>{`Now that you're mapping routes to components, you need those components :)`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Default>`}</inlineCode>{` component/route can be simple:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=const%20Default%20%3D%20(%7B%20element%2C%20...props%20%7D)%20%3D%3E%20(%0A%20%20%3CLayout%20authenticated%3D%7Bfalse%7D%20authorized%3D%7Bfalse%7D%20fullwidth%3D%7Btrue%7D%20%7B...props%7D%3E%0A%20%20%20%20%7Belement%7D%0A%20%20%3C%2FLayout%3E%0A)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "734px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.97757847533632%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'141\\'%20viewBox=\\'0%200%20400%20141\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%2028c-2%203-1%2085%200%2087%202%202%20362%202%20364%200%201-2%202-84%200-87S20%2025%2018%2028\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d6af15b04bf3c5a39c10bab5c34d0486/ca0a1/techletter.app-screenshot-1592236046978.webp 223w", "/static/d6af15b04bf3c5a39c10bab5c34d0486/75680/techletter.app-screenshot-1592236046978.webp 445w", "/static/d6af15b04bf3c5a39c10bab5c34d0486/a242a/techletter.app-screenshot-1592236046978.webp 734w"],
                "sizes": "(max-width: 734px) 100vw, 734px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d6af15b04bf3c5a39c10bab5c34d0486/e92b6/techletter.app-screenshot-1592236046978.png 223w", "/static/d6af15b04bf3c5a39c10bab5c34d0486/e66bf/techletter.app-screenshot-1592236046978.png 445w", "/static/d6af15b04bf3c5a39c10bab5c34d0486/c6d67/techletter.app-screenshot-1592236046978.png 734w"],
                "sizes": "(max-width: 734px) 100vw, 734px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d6af15b04bf3c5a39c10bab5c34d0486/c6d67/techletter.app-screenshot-1592236046978.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Tells the layout that you're not authenticated, not authorized, and in my case to render the page fullwidth without the sidebar.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Layout>`}</inlineCode>{` could check for this stuff with `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` but it's cleaner to just tell it. `}<em parentName="p">{`"Hey, show extra buttons for authorized users"`}</em></p>
    <h3 {...{
      "id": "4-scoped-route"
    }}>{`4. scoped route`}</h3>
    <p><inlineCode parentName="p">{`<ScopedRoute>`}</inlineCode>{` is the bread and butter of this approach. And unlike my previous attempt, it's not gnarly at all ✌️`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=export%20const%20ScopedRoute%20%3D%20(%7B%20scopes%2C%20element%2C%20...props%20%7D)%20%3D%3E%20%7B%0A%20%20const%20%7B%20isAuthenticated%2C%20isAuthorized%20%7D%20%3D%20useAuth()%0A%0A%20%20if%20(isAuthorized(scopes))%20%7B%0A%20%20%20%20return%20(%0A%20%20%20%20%20%20%3CLayout%20authorized%3D%7Btrue%7D%20fullwidth%3D%7Bfalse%7D%20%7B...props%7D%3E%0A%20%20%20%20%20%20%20%20%7Belement%7D%0A%20%20%20%20%20%20%3C%2FLayout%3E%0A%20%20%20%20)%0A%20%20%7D%20else%20if%20(isAuthenticated())%20%7B%0A%20%20%20%20return%20(%0A%20%20%20%20%20%20%3CLayout%20authorized%3D%7Bfalse%7D%20fullwidth%3D%7Btrue%7D%20%7B...props%7D%3E%0A%20%20%20%20%20%20%20%20%3CPleasePurchase%20%2F%3E%0A%20%20%20%20%20%20%3C%2FLayout%3E%0A%20%20%20%20)%0A%20%20%7D%20else%20%7B%0A%20%20%20%20return%20(%0A%20%20%20%20%20%20%3CLayout%20authorized%3D%7Bfalse%7D%20fullwidth%3D%7Btrue%7D%20%7B...props%7D%3E%0A%20%20%20%20%20%20%20%20%3CPleaseLogin%20%2F%3E%0A%20%20%20%20%20%20%3C%2FLayout%3E%0A%20%20%20%20)%0A%20%20%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "606px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "95.96412556053812%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'384\\'%20viewBox=\\'0%200%20400%20384\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M22%2034c-2%203-1%20318%200%20319%202%202%20354%202%20356%200%201-1%202-316%200-319-1-3-355-3-356%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/665ee85659f5d7c6d716730352dfec2d/ca0a1/techletter.app-screenshot-1592236046808.webp 223w", "/static/665ee85659f5d7c6d716730352dfec2d/75680/techletter.app-screenshot-1592236046808.webp 445w", "/static/665ee85659f5d7c6d716730352dfec2d/18e7e/techletter.app-screenshot-1592236046808.webp 606w"],
                "sizes": "(max-width: 606px) 100vw, 606px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/665ee85659f5d7c6d716730352dfec2d/e92b6/techletter.app-screenshot-1592236046808.png 223w", "/static/665ee85659f5d7c6d716730352dfec2d/e66bf/techletter.app-screenshot-1592236046808.png 445w", "/static/665ee85659f5d7c6d716730352dfec2d/4d4a2/techletter.app-screenshot-1592236046808.png 606w"],
                "sizes": "(max-width: 606px) 100vw, 606px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/665ee85659f5d7c6d716730352dfec2d/4d4a2/techletter.app-screenshot-1592236046808.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Hook into user state with `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` and render different pages when they're authenticated, authorized, or unknown.`}</p>
    <p>{`Flags in `}<inlineCode parentName="p">{`<Layout>`}</inlineCode>{` change some UI features and the child element being `}<inlineCode parentName="p">{`{element}`}</inlineCode>{` – the MDX content – or purchase/login specifies the core of the page.`}</p>
    <p>{`And that's how you get elegant scope-based permissions in your JAMStack app ✌️`}</p>
    <p>{`Happy Monday`}</p>
    <p>{`Cheers, ~Swizec`}</p>
    <p>{`PS: scopes get into your users `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/add-granular-role-based-access-to-your-jamstack-app/swizec/9418"
      }}>{`the same way roles do`}</a>{`. You add them through the Auth0 API or UI and use a bit of Auth0 JavaScript to inject it into user properties on every request.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      